/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { TESTIDS } from "@/constants/testIds";
import { Plus, Trash2, AlertTriangle, Truck, Pencil, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Link, useSearchParams } from "react-router-dom";
import BulkActionBar from "@/components/BulkActionBar";
import BulkEditModal from "@/components/BulkEditModal";
import Modal from "@/components/Modal";
import ImportExportButtons from "@/components/ImportExportButtons";
import ColumnChooser from "@/components/ColumnChooser";
import { useColumnPrefs } from "@/hooks/useColumnPrefs";

const INVENTORY_BULK_FIELDS = [
  { name: "Category", label: "Category", type: "text" },
  { name: "MSRP", label: "MSRP ($)", type: "number" },
  { name: "Min_Stock_Warning", label: "Min Stock Warning", type: "number" },
  { name: "Total_Stock", label: "Total Stock", type: "number" },
  { name: "Allocated_Stock", label: "Allocated Stock", type: "number" },
  { name: "Unit_Weight_KG", label: "Unit Weight (kg)", type: "number" },
  { name: "Units_Per_Pallet", label: "Units / Pallet", type: "number" },
];

function InventoryNumberCell({ item, field, canManage, onSaved, className = "" }) {
  const toast = useToast();
  const stored = Number(item[field] ?? 0);
  const [value, setValue] = useState(String(stored));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!canManage) {
    return <span className={`font-mono ${className}`}>{stored.toLocaleString()}</span>;
  }

  const displayValue = editing ? value : String(stored);

  const commit = async () => {
    setEditing(false);
    const next = Number(value);
    if (Number.isNaN(next) || next === stored) {
      setValue(String(stored));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/inventory/${item.id}`, { [field]: next });
      onSaved?.(data);
      setValue(String(Number(data[field] ?? next)));
      toast.success(`${field.replace(/_/g, " ")} updated`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setValue(String(stored));
    } finally {
      setSaving(false);
    }
  };

  return (
    <input
      data-testid={`inventory-inline-${field}-${item.id}`}
      type="number"
      step="any"
      value={displayValue}
      disabled={saving}
      onFocus={() => { setEditing(true); setValue(String(stored)); }}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
        if (e.key === "Escape") { setValue(String(stored)); setEditing(false); e.currentTarget.blur(); }
      }}
      className={`w-24 text-center font-mono text-sm bg-transparent border border-transparent hover:border-zinc-300 focus:border-brand-500 focus:bg-white focus:outline-none rounded px-2 py-1 transition-colors ${saving ? "opacity-50" : ""} ${className}`}
    />
  );
}

function StockBar({ total, allocated, min }) {
  const available = Math.max(0, total - allocated);
  const pctAlloc = total > 0 ? (allocated / total) * 100 : 0;
  const low = available < min;
  return (
    <div className="w-44">
      <div className="h-2.5 bg-zinc-100 rounded-full flex overflow-hidden">
        <div className={low ? "bg-red-500" : "bg-green-500"} style={{ width: `${100 - pctAlloc}%` }} />
        <div className="bg-zinc-400" style={{ width: `${pctAlloc}%` }} />
      </div>
      <div className="flex justify-between text-[10px] font-mono mt-1 text-zinc-500">
        <span className={low ? "text-red-600 font-bold flex items-center gap-1" : ""}>
          {low && <AlertTriangle className="w-3 h-3" />} avail {available.toLocaleString()}
        </span>
        <span>alloc {allocated.toLocaleString()}</span>
      </div>
    </div>
  );
}

export default function InventoryPage() {
  const { user } = useAuth();
  const toast = useToast();
  const canManage = ["admin", "user"].includes(user.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const lowOnly = searchParams.get("filter") === "low";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);  // product being edited
  const [showBulk, setShowBulk] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const clearLowFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  };

  const visibleItems = lowOnly
    ? items.filter((it) => (Number(it.Total_Stock || 0) - Number(it.Allocated_Stock || 0)) <= Number(it.Min_Stock_Warning || 0))
    : items;

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/inventory"); setItems(data); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/inventory/${id}`); setItems(it => it.filter(i => i.id !== id)); toast.success("Product deleted."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const allSelected = items.length > 0 && items.every((it) => selected.has(it.id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) items.forEach((it) => n.delete(it.id));
    else items.forEach((it) => n.add(it.id));
    return n;
  });
  const clearSelection = () => setSelected(new Set());

  const applyBulk = async (patch) => {
    const payload = { items: Array.from(selected).map((id) => ({ id, ...patch })) };
    try {
      const { data } = await api.post("/bulk/inventory", payload);
      toast.success(`Updated ${data.updated_count} product(s).`);
      if (data.errors?.length) toast.error(`${data.errors.length} record(s) failed.`);
      clearSelection();
      setShowBulk(false);
      load();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(msg);
      throw new Error(msg);
    }
  };

  const updateItem = (p) => setItems((prev) => prev.map((x) => x.id === p.id ? p : x));

  // Column descriptors — drive both the header and the cells. `locked: true`
  // means the user cannot hide/reorder it (e.g. selection + actions).
  const allColumns = [
    ...(canManage ? [{
      key: "_select", label: "Select", locked: true, align: "left", width: "w-10",
      header: () => (
        <input
          type="checkbox"
          data-testid="inventory-select-all"
          checked={allSelected}
          onChange={toggleAll}
        />
      ),
      cell: (it) => (
        <input
          type="checkbox"
          data-testid={`inventory-select-${it.id}`}
          checked={selected.has(it.id)}
          onChange={() => toggleOne(it.id)}
        />
      ),
    }] : []),
    { key: "Product_Name", label: "Product", cell: (it) => <div className="font-semibold">{it.Product_Name}</div> },
    { key: "SKU", label: "SKU", cell: (it) => <span className="font-mono text-xs">{it.SKU || "—"}</span> },
    { key: "Category", label: "Category", cell: (it) => it.Category || "—" },
    { key: "Colour", label: "Colour", cell: (it) => it.Colour || "—" },
    {
      key: "Total_Stock", label: "Total stock", align: "center",
      cell: (it) => (
        <InventoryNumberCell item={it} field="Total_Stock" canManage={canManage} onSaved={updateItem} />
      ),
    },
    {
      key: "Pallets", label: "Pallets", align: "center",
      cell: (it) => (
        <span className="font-mono text-xs text-zinc-500">
          {it.Units_Per_Pallet > 0 ? Math.floor((it.Total_Stock || 0) / it.Units_Per_Pallet) : "—"}
        </span>
      ),
    },
    {
      key: "Allocated_Stock", label: "Allocated", align: "center",
      cell: (it) => (
        <InventoryNumberCell item={it} field="Allocated_Stock" canManage={canManage} onSaved={updateItem} />
      ),
    },
    {
      key: "Available", label: "Available", align: "center",
      cell: (it) => {
        const isLow = (it.Total_Stock - (it.Allocated_Stock || 0)) <= (it.Min_Stock_Warning || 0);
        return (
          <span
            className={`font-mono font-semibold inline-flex items-center justify-center gap-1 text-green-600 ${isLow ? "border-b border-dashed border-amber-400" : ""}`}
            title={isLow ? "Below minimum stock warning" : ""}
          >
            {isLow && <AlertTriangle className="w-3 h-3 text-amber-500" />}
            {Math.max(0, (it.Total_Stock || 0) - (it.Allocated_Stock || 0))}
          </span>
        );
      },
    },
    {
      key: "Min_Stock_Warning", label: "Min warning", align: "center",
      cell: (it) => (
        <InventoryNumberCell item={it} field="Min_Stock_Warning" canManage={canManage} onSaved={updateItem} className="text-red-600" />
      ),
    },
    { key: "MSRP", label: "MSRP ($)", align: "center", defaultVisible: false, cell: (it) => <span className="font-mono">${Number(it.MSRP || 0).toFixed(2)}</span> },
    { key: "Unit_Weight_KG", label: "Unit weight (kg)", align: "center", defaultVisible: false, cell: (it) => <span className="font-mono">{Number(it.Unit_Weight_KG || 0).toFixed(1)}</span> },
    { key: "Units_Per_Pallet", label: "Units / Pallet", align: "center", defaultVisible: false, cell: (it) => <span className="font-mono">{it.Units_Per_Pallet || "—"}</span> },
    ...(canManage ? [{
      key: "_actions", label: "Actions", locked: true, align: "right",
      cell: (it) => (
        <div className="flex items-center justify-end gap-1">
          <button
            data-testid={`inventory-edit-${it.id}`}
            onClick={() => setEditing(it)}
            className="text-zinc-400 hover:text-brand-700 p-1"
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
          {user.role === "admin" && (
            <button onClick={() => onDelete(it.id)} className="text-zinc-400 hover:text-red-600 p-1" title="Delete">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    }] : []),
  ];

  const { visibleColumns, orderedColumns, isVisible, toggleColumn, moveColumn, reset } =
    useColumnPrefs("inventory", allColumns);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Inventory</h1>
          <p className="text-xs sm:text-sm text-zinc-500 mt-1">{items.length} SKU{items.length === 1 ? "" : "s"} tracked across the yard.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ColumnChooser
            tableLabel="inventory"
            orderedColumns={orderedColumns}
            isVisible={isVisible}
            toggleColumn={toggleColumn}
            moveColumn={moveColumn}
            reset={reset}
          />
          {canManage && (
            <>
              <Link
                to="/settings/delivery-zones"
                data-testid="inventory-delivery-zones-link"
                className="inline-flex items-center justify-center gap-2 h-10 px-3 sm:px-4 bg-white text-zinc-700 text-sm font-semibold rounded-lg border border-zinc-300 hover:border-brand-500 hover:text-brand-700 transition-colors"
                title="Manage delivery zones (postcode → cost)"
                aria-label="Delivery Zones"
              >
                <Truck className="w-4 h-4" /> <span className="hidden sm:inline">Delivery Zones</span>
              </Link>
              <ImportExportButtons
                entity="inventory"
                rows={items.map(({ id, _id, created_date, updated_date, ...rest }) => rest)}
                exportColumns={[
                  "Product_Name", "SKU", "Category", "Colour", "Unit_Weight_KG", "Units_Per_Pallet",
                  "Total_Stock", "Allocated_Stock", "Min_Stock_Warning", "MSRP",
                ]}
                exportName={`edenstone-inventory-${new Date().toISOString().slice(0, 10)}`}
                onImported={load}
              />
              <button data-testid={TESTIDS.newInventoryBtn} onClick={() => setShowNew(true)} className="es-btn-primary h-10 px-3 sm:px-4">
                <Plus className="w-4 h-4" /> <span className="hidden sm:inline">New product</span>
              </button>
            </>
          )}
        </div>
      </div>

      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}

      {lowOnly && (
        <div
          data-testid="inventory-low-filter-banner"
          className="es-card p-3 flex items-center justify-between bg-amber-50 border-amber-200"
        >
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="font-semibold">Showing only below-minimum stock</span>
            <span className="text-amber-700">· {visibleItems.length} of {items.length} products</span>
          </div>
          <button
            onClick={clearLowFilter}
            className="text-xs font-semibold text-amber-900 hover:text-amber-700 inline-flex items-center gap-1"
            data-testid="inventory-low-filter-clear"
          >
            <X className="w-3.5 h-3.5" /> Clear filter
          </button>
        </div>
      )}

      <div className="es-card overflow-x-auto -mx-4 sm:mx-0 sm:rounded-xl">
        <table className="es-table min-w-[760px]">
          <thead>
            <tr>
              {visibleColumns.map((c) => (
                <th
                  key={c.key}
                  className={[
                    c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "",
                    c.width || "",
                  ].join(" ")}
                >
                  {c.header ? c.header() : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={visibleColumns.length} className="text-center py-10 font-mono text-xs text-zinc-500">Loading…</td></tr>}
            {!loading && visibleItems.length === 0 && <tr><td colSpan={visibleColumns.length} className="text-center py-10 font-mono text-xs text-zinc-500">{lowOnly ? "No products are below their minimum stock warning." : "No inventory yet."}</td></tr>}
            {visibleItems.map((it) => (
              <tr key={it.id} data-testid={TESTIDS.inventoryRow(it.id)} className={selected.has(it.id) ? "bg-brand-50" : ""}>
                {visibleColumns.map((c) => (
                  <td key={c.key} className={c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : ""}>{c.cell(it)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewInventoryModal onClose={() => setShowNew(false)} onCreated={(p) => { setItems(prev => [p, ...prev]); setShowNew(false); }} />}
      {editing && (
        <NewInventoryModal
          initial={editing}
          onClose={() => setEditing(null)}
          onUpdated={(p) => { setItems(prev => prev.map(x => x.id === p.id ? p : x)); setEditing(null); }}
        />
      )}

      {canManage && (
        <BulkActionBar
          count={selected.size}
          label={selected.size === 1 ? "product selected" : "products selected"}
          onEdit={() => setShowBulk(true)}
          onClear={clearSelection}
        />
      )}
      {showBulk && (
        <BulkEditModal
          title="Bulk Edit Inventory"
          fields={INVENTORY_BULK_FIELDS}
          selectedCount={selected.size}
          onClose={() => setShowBulk(false)}
          onApply={applyBulk}
        />
      )}
    </div>
  );
}

export function NewInventoryModal({ onClose, onCreated, initial = null, onUpdated }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState(() => ({
    Product_Name: initial?.Product_Name || "",
    SKU: initial?.SKU || "",
    Category: initial?.Category || "",
    Colour: initial?.Colour || "",
    Total_Stock: initial?.Total_Stock ?? 0,
    Allocated_Stock: initial?.Allocated_Stock ?? 0,
    Min_Stock_Warning: initial?.Min_Stock_Warning ?? 100,
    MSRP: initial?.MSRP ?? 0,
    Unit_Weight_KG: initial?.Unit_Weight_KG ?? 0,
    Units_Per_Pallet: initial?.Units_Per_Pallet ?? 0,
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      const payload = { ...form };
      ["Total_Stock", "Allocated_Stock", "Min_Stock_Warning", "MSRP", "Unit_Weight_KG", "Units_Per_Pallet"].forEach(k => payload[k] = Number(payload[k]));
      if (isEdit) {
        const { data } = await api.patch(`/inventory/${initial.id}`, payload);
        onUpdated ? onUpdated(data) : onCreated?.(data);
      } else {
        const { data } = await api.post("/inventory", payload);
        onCreated?.(data);
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Modal title={isEdit ? `Edit: ${initial.Product_Name}` : "New Product"} onClose={onClose}>
      <form onSubmit={onSubmit} className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="es-label">Product Name *</label>
            <input data-testid="inv-Product_Name" required value={form.Product_Name} onChange={e => u("Product_Name", e.target.value)} className="es-input" />
          </div>
          <div><label className="es-label">SKU</label><input data-testid="inv-SKU" value={form.SKU} onChange={e => u("SKU", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Category</label><input data-testid="inv-Category" value={form.Category} onChange={e => u("Category", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Colour</label><input data-testid="inv-Colour" value={form.Colour} onChange={e => u("Colour", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Unit weight (kg)</label><input data-testid="inv-Unit_Weight_KG" type="number" step="0.1" value={form.Unit_Weight_KG} onChange={e => u("Unit_Weight_KG", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Units per pallet</label><input data-testid="inv-Units_Per_Pallet" type="number" value={form.Units_Per_Pallet} onChange={e => u("Units_Per_Pallet", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Total stock</label><input data-testid="inv-Total_Stock" type="number" value={form.Total_Stock} onChange={e => u("Total_Stock", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Allocated stock</label><input data-testid="inv-Allocated_Stock" type="number" value={form.Allocated_Stock} onChange={e => u("Allocated_Stock", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">Min stock warning</label><input data-testid="inv-Min_Stock_Warning" type="number" value={form.Min_Stock_Warning} onChange={e => u("Min_Stock_Warning", e.target.value)} className="es-input" /></div>
          <div><label className="es-label">MSRP ($)</label><input data-testid="inv-MSRP" type="number" step="0.01" value={form.MSRP} onChange={e => u("MSRP", e.target.value)} className="es-input" /></div>

          {err && <div className="sm:col-span-2 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}

          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="es-btn-primary">{saving ? "Saving…" : (isEdit ? "Save changes" : "Create product")}</button>
          </div>
      </form>
    </Modal>
  );
}
